import React, { useEffect, useRef, forwardRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/RouteMap.css';

// Fix per a les icones de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const createCustomIcon = (type) => {
  const colors = {
    drinking_water: '#1e90ff',
    shelter: '#ff6347',
    viewpoint: '#32cd32',
    peak: '#ff4500',
    picnic_site: '#8b4513',
    restaurant: '#ffa500',
    cafe: '#d2691e',
    parking: '#808080',
    toilets: '#9370db',
    spring: '#00bfff',
    bicycle: '#ff1493',
    default: '#667eea',
  };

  const color = colors[type] || colors.default;

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Component intern per gestionar el marcador hover (el punt que es mou en fer hover a la gràfica)
const HoverMarker = ({ mapRef }) => {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (mapRef) {
      mapRef.current = {
        updateHoverPosition: (km, coordinates) => {
          if (km === null) {
            if (markerRef.current) {
              map.removeLayer(markerRef.current);
              markerRef.current = null;
            }
            return;
          }

          let totalDistance = 0;
          let targetCoord = coordinates[0];

          for (let i = 1; i < coordinates.length; i++) {
            const prev = coordinates[i - 1];
            const curr = coordinates[i];
            
            const R = 6371;
            const dLat = (curr.lat - prev.lat) * Math.PI / 180;
            const dLon = (curr.lon - prev.lon) * Math.PI / 180;
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                      Math.cos(prev.lat * Math.PI / 180) * Math.cos(curr.lat * Math.PI / 180) *
                      Math.sin(dLon/2) * Math.sin(dLon/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            totalDistance += R * c;

            if (totalDistance >= km) {
              targetCoord = curr;
              break;
            }
          }

          if (!markerRef.current) {
            markerRef.current = L.circleMarker([targetCoord.lat, targetCoord.lon], {
              radius: 10,
              fillColor: '#f59e0b',
              color: 'white',
              weight: 3,
              opacity: 1,
              fillOpacity: 0.9
            }).addTo(map);
          } else {
            markerRef.current.setLatLng([targetCoord.lat, targetCoord.lon]);
          }
        }
      };
    }
  }, [map, mapRef]);

  return null;
};

const RouteMap = forwardRef(({ coordinates, pois, stravaSegments }, ref) => {
  const mapRef = useRef();
  const routeCoords = coordinates.map((coord) => [coord.lat, coord.lon]);
  
  // Centre per defecte si no hi ha ruta
  const center = routeCoords.length > 0 ? routeCoords[Math.floor(routeCoords.length / 2)] : [41.3851, 2.1734];

  useEffect(() => {
    if (mapRef.current && routeCoords.length > 0) {
      const bounds = L.latLngBounds(routeCoords);
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [routeCoords]);

  return (
    <div className="route-map-container fade-in">
      <MapContainer center={center} zoom={13} ref={mapRef} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <HoverMarker mapRef={ref} />

        {/* Ruta principal */}
        <Polyline positions={routeCoords} color="#667eea" weight={5} opacity={0.7} />

        {/* Marcadors inici/final */}
        {routeCoords.length > 0 && (
          <>
            <Marker position={routeCoords[0]}>
              <Popup><strong>🚀 Inici de la ruta</strong></Popup>
            </Marker>
            <Marker position={routeCoords[routeCoords.length - 1]}>
              <Popup><strong>🏁 Final de la ruta</strong></Popup>
            </Marker>
          </>
        )}

        {/* SEGMENTS STRAVA - CORREGIT I ROBUST */}
        {stravaSegments && stravaSegments.length > 0 && (
          <>
            {stravaSegments.slice(0, 15).map((segment, index) => {
              // Verificació d'existència de coordenades d'inici
              if (!segment.start_lat || !segment.start_lon) return null;
              
              const startPos = [segment.start_lat, segment.start_lon];
              const endPos = (segment.end_lat && segment.end_lon) 
                ? [segment.end_lat, segment.end_lon] 
                : null;
              
              return (
                <React.Fragment key={`strava-group-${segment.id || index}`}>
                  {/* Línia del segment (només si tenim punt final) */}
                  {endPos && (
                    <Polyline
                      positions={[startPos, endPos]}
                      pathOptions={{
                        color: '#fc4c02',
                        weight: 6,
                        opacity: 0.8,
                        dashArray: '10, 8'
                      }}
                    />
                  )}

                  {/* Punt d'inici clicable */}
                  <CircleMarker
                    center={startPos}
                    radius={7}
                    pathOptions={{
                      color: 'white',
                      fillColor: '#fc4c02',
                      fillOpacity: 1,
                      weight: 2
                    }}
                  >
                    <Popup>
                      <div className="strava-popup">
                        <strong style={{ color: '#fc4c02' }}>🏃 {segment.name || 'Segment de Strava'}</strong>
                        <div style={{ marginTop: '5px' }}>
                          <p style={{ margin: '2px 0' }}>📏 Distància: {(segment.distance || 0).toFixed(0)}m</p>
                          <p style={{ margin: '2px 0' }}>📈 Pendent: {(segment.avg_grade || 0).toFixed(1)}%</p>
                          {segment.kom_time_sec && (
                            <p style={{ margin: '2px 0', fontWeight: 'bold' }}>
                              🏆 Rècord: {Math.floor(segment.kom_time_sec / 60)}:{(segment.kom_time_sec % 60).toString().padStart(2, '0')}s
                            </p>
                          )}
                          {segment.start_km !== undefined && (
                            <p style={{ margin: '2px 0', color: '#666' }}>📍 Apareix al KM {segment.start_km}</p>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                </React.Fragment>
              );
            })}
          </>
        )}

        {/* POIs (Punts d'interès) */}
        {pois && pois.map((poi, index) => (
          <Marker
            key={`poi-${index}`}
            position={[poi.lat, poi.lon]}
            icon={createCustomIcon(poi.type)}
          >
            <Popup>
              <div className="poi-popup">
                <strong>{poi.name}</strong>
                <p>Tipus: {poi.type}</p>
                {poi.distance_from_start && <p>KM: {poi.distance_from_start.toFixed(1)}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Llegenda del mapa */}
      <div className="map-legend">
        <div className="legend-item">
          <div className="legend-line route-line"></div>
          <span>Ruta principal</span>
        </div>
        {stravaSegments && stravaSegments.length > 0 && (
          <div className="legend-item">
            <div className="legend-line strava-line" style={{ borderBottom: '3px dashed #fc4c02' }}></div>
            <span>Segments Strava ({stravaSegments.length})</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default RouteMap;